"use client";

import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useTranslation } from 'react-i18next';
import './FeaturedSectionStats.css';
import { TextShimmerAlt } from './text-shimmer-alt';

interface StatItem {
  value: string;
  label: string;
  translationKey?: string;
  labelTranslationKey?: string;
}

interface ChartDataItem {
  name: string;
  value: number;
}

interface FeaturedSectionStatsProps {
  title?: string;
  titleTranslationKey?: string;
  subtitle?: string;
  subtitleTranslationKey?: string;
  stats?: StatItem[];
  imageSrc?: string;
  imageAlt?: string;
  chartData?: ChartDataItem[];
  showChart?: boolean;
}

export default function FeaturedSectionStats({
  title,
  titleTranslationKey = 'stats.title',
  subtitle,
  subtitleTranslationKey = 'stats.subtitle',
  stats,
  imageSrc,
  imageAlt = "Featured image",
  chartData,
  showChart = true
}: FeaturedSectionStatsProps) {
  const { t } = useTranslation();

  const defaultData = [
    { name: "Jan", value: 85 },
    { name: "Feb", value: 90 },
    { name: "Mar", value: 95 },
    { name: "Apr", value: 92 },
    { name: "May", value: 98 },
    { name: "Jun", value: 99 },
    { name: "Jul", value: 110 },
  ];

  const defaultStats = [
    {
      value: '1,000+',
      label: 'Entreprises protégées',
      translationKey: 'stats.clients',
      labelTranslationKey: 'stats.clientsLabel'
    },
    {
      value: '99.9%',
      label: 'Disponibilité garantie',
      translationKey: 'stats.uptime',
      labelTranslationKey: 'stats.uptimeLabel'
    },
    {
      value: '< 2h',
      label: 'Temps de réponse cyber',
      translationKey: 'stats.incidents',
      labelTranslationKey: 'stats.incidentsLabel'
    },
    {
      value: '25+',
      label: 'Certifications Azure',
      translationKey: 'stats.certifications',
      labelTranslationKey: 'stats.certificationsLabel'
    }
  ];

  const displayStats = stats || defaultStats;
  const displayData = chartData || defaultData;

  return (
    <section className="tw-text-left tw-pb-[4em] max-[734px]:tw-pb-0" style={{ width: "100%", maxWidth: "1648px", margin: "0 auto" }}>
      <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-8 tw-items-start">
        <div className="intro-text-alignment tw-w-full lg:tw-w-[1268px]">
         <TextShimmerAlt
            as="h3"
            duration={3}
            className="tw-text-lg sm:tw-text-xl lg:tw-text-4xl tw-font-medium tw-text-gray-900 dark:tw-text-white tw-mb-16 featured-title-mobile"
          >
            {title || t(titleTranslationKey, "Des performances qui parlent d'elles-mêmes.")}{' '}
            <span className="tw-text-gray-500 dark:tw-text-gray-400 tw-text-sm sm:tw-text-base lg:tw-text-4xl featured-subtitle-mobile">
              {subtitle || t(subtitleTranslationKey, "Nos solutions cloud et cybersécurité protègent plus de 1000 entreprises avec une expertise reconnue depuis 2018.")}
            </span>
        </TextShimmerAlt>


        </div>

        {/* Image Section - Now on the right */}

      </div>

      {/* Area Chart */}
      {showChart && (
        <div className="tw-w-full tw-h-48 tw-mt-8">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={displayData}>
              <defs>
                <linearGradient id="colorItmBlue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#30b1cb" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#4166bc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#30b1cb"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorItmBlue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}